// src/dashboard.js - MINIMAL FIX (ONLY TAX RATE SOURCE CHANGED)
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, BarChart3, DollarSign, FileText, TrendingUp, Wallet } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
import AutoFitText from './components/ui/AutoFitText';
import OnboardingPopup from './OnboardingPopup';

/* ===============================
   HELPERS
================================== */

const money = (n) => Number(n || 0).toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const StatCardIcon = ({ type, size }) => {
  const shared = { width: size, height: size, flex: '0 0 auto' };

  if (type === 'invoice') {
    return (
      <span aria-hidden="true" style={{ ...shared, position: 'relative', display: 'inline-block' }}>
        <FileText size={size} color="white" strokeWidth={2.4} />
        <DollarSign
          size={Math.round(size * 0.5)}
          color="white"
          strokeWidth={3}
          style={{ position: 'absolute', right: -2, bottom: -2 }}
        />
      </span>
    );
  }

  if (type === 'profit') {
    return (
      <span aria-hidden="true" style={{ ...shared, position: 'relative', display: 'inline-block' }}>
        <BarChart3 size={size} color="white" strokeWidth={2.4} />
        <TrendingUp
          size={Math.round(size * 0.72)}
          color="white"
          strokeWidth={2.8}
          style={{ position: 'absolute', right: -3, top: -4 }}
        />
      </span>
    );
  }

  if (type === 'overdue') {
    return (
      <AlertTriangle
        aria-hidden="true"
        size={size}
        color="#8f0d14"
        fill="white"
        strokeWidth={2.8}
        style={shared}
      />
    );
  }

  if (type === 'expenses') {
    return <Wallet aria-hidden="true" size={size} color="white" strokeWidth={2.4} style={shared} />;
  }

  if (type === 'mileage') {
    return (
      <svg aria-hidden="true" viewBox="0 0 48 48" style={shared}>
        <path d="M16 4h16l11 40H5L16 4Z" fill="white" />
        <path d="M24 8v7m0 6v7m0 6v7" fill="none" stroke="#005443" strokeWidth="3" strokeLinecap="round" />
      </svg>
    );
  }

  return <FileText aria-hidden="true" size={size} color="white" strokeWidth={2.4} style={shared} />;
};

const toLocalYMD = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toStartOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const parseDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const getDueDate = (inv) => inv.due_date || inv.dueDate || inv.due || null;

const getCreatedAt = (inv) => {
  return (
    inv.created_at ||
    inv.createdAt ||
    inv.date_created ||
    inv.dateCreated ||
    inv.created ||
    inv.invoice_date ||
    inv.invoiceDate ||
    null
  );
};

const isInvoiceOverdue = (inv) => {
  const total = Number(inv.total || 0);
  const paid = Number(inv.amount_paid || inv.paid_amount || 0);
  const balance = Number(inv.balance_due ?? Math.max(0, total - paid));

  if ((inv.status || '').toLowerCase() === 'paid' || balance <= 0) return false;

  const createdAt = parseDate(getCreatedAt(inv)) || new Date();
  const due = parseDate(getDueDate(inv)) || createdAt;
  const today = toStartOfDay(new Date());
  const dueDay = toStartOfDay(due);

  return today.getTime() > dueDay.getTime() && balance > 0;
};

const currentYear = new Date().getFullYear();

const localDateKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const nthWeekdayOfMonth = (year, month, weekday, occurrence) => {
  const first = new Date(year, month, 1);
  const offset = (weekday - first.getDay() + 7) % 7;
  return new Date(year, month, 1 + offset + (occurrence - 1) * 7);
};

const lastWeekdayOfMonth = (year, month, weekday) => {
  const last = new Date(year, month + 1, 0);
  const offset = (last.getDay() - weekday + 7) % 7;
  return new Date(year, month, last.getDate() - offset);
};

const observedFixedHoliday = (year, month, day) => {
  const date = new Date(year, month, day);
  if (date.getDay() === 6) date.setDate(date.getDate() - 1);
  if (date.getDay() === 0) date.setDate(date.getDate() + 1);
  return date;
};

const easterSunday = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month, day);
};

const getDashboardHoliday = (date) => {
  const key = localDateKey(date);
  const year = date.getFullYear();
  const federalHolidays = [];
  [year - 1, year, year + 1].forEach((holidayYear) => {
    federalHolidays.push(
      ['New Year\'s Day', observedFixedHoliday(holidayYear, 0, 1)],
      ['Juneteenth National Independence Day', observedFixedHoliday(holidayYear, 5, 19)],
      ['Independence Day', observedFixedHoliday(holidayYear, 6, 4)],
      ['Veterans Day', observedFixedHoliday(holidayYear, 10, 11)],
      ['Christmas Day', observedFixedHoliday(holidayYear, 11, 25)],
      ['Martin Luther King Jr. Day', nthWeekdayOfMonth(holidayYear, 0, 1, 3)],
      ['Washington\'s Birthday', nthWeekdayOfMonth(holidayYear, 1, 1, 3)],
      ['Memorial Day', lastWeekdayOfMonth(holidayYear, 4, 1)],
      ['Labor Day', nthWeekdayOfMonth(holidayYear, 8, 1, 1)],
      ['Columbus Day', nthWeekdayOfMonth(holidayYear, 9, 1, 2)],
      ['Thanksgiving Day', nthWeekdayOfMonth(holidayYear, 10, 4, 4)]
    );
  });
  const federal = federalHolidays.find(([, holidayDate]) => localDateKey(holidayDate) === key);
  if (federal) return federal[0];

  if (localDateKey(easterSunday(year)) === key) return 'Easter Sunday';

  const observances = [
    ['Valentine\'s Day', new Date(year, 1, 14)],
    ['Mother\'s Day', nthWeekdayOfMonth(year, 4, 0, 2)],
    ['Father\'s Day', nthWeekdayOfMonth(year, 5, 0, 3)],
    ['Halloween', new Date(year, 9, 31)],
    ['National Dog Day', new Date(year, 7, 26)],
    ['National Candy Day', new Date(year, 10, 4)]
  ];
  const observance = observances.find(([, observanceDate]) => localDateKey(observanceDate) === key);
  return observance ? observance[0] : '';
};

const formatDashboardDate = (date) => new Intl.DateTimeFormat('en-US', {
  weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
}).format(date);


const getEstimateStatusAny = (e) => (
  e.status ||
  e.estimate_status ||
  e.estimateStatus ||
  e.state ||
  ''
);

const isEstimatePending = (e) => {
  const s = String(getEstimateStatusAny(e)).toLowerCase().trim();

  if (s === 'approved') return false;
  if (s === 'accepted') return false;
  if (s === 'converted') return false;
  if (s === 'invoiced') return false;
  if (s === 'declined') return false;
  if (s === 'rejected') return false;
  if (s === 'canceled' || s === 'cancelled') return false;

  if (s === 'pending') return true;
  if (s === 'draft') return true;
  if (s === 'sent') return true;
  if (s === 'open') return true;

  return !s;
};

const Dashboard = ({ navigate, soundEnabled, onSoundEnabledChange }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  const [selectedYear, setSelectedYear] = useState(currentYear);

  const [businessName, setBusinessName] = useState('MYTOOL NOW ADMIN');

  const [invoices, setInvoices] = useState([]);
  const [monthlyExpenses, setMonthlyExpenses] = useState(() => Array(12).fill(0));
  const [estimates, setEstimates] = useState([]);
  const [inventoryAlerts, setInventoryAlerts] = useState(null);
  const [inventoryValue, setInventoryValue] = useState(0);
  const [inventoryAlertsError, setInventoryAlertsError] = useState(false);
  const [taxCenterReport, setTaxCenterReport] = useState(null);
  const [appointmentsToday, setAppointmentsToday] = useState(0);
  const [dashboardToday, setDashboardToday] = useState(() => new Date());

  const [showOnboarding, setShowOnboarding] = useState(false);

  const revChartRef = useRef(null);
  const taxReportRequestRef = useRef(0);
  const expenseChartRequestRef = useRef(0);
  const appointmentCountRequestRef = useRef(0);
  const inventoryAlertsRequestRef = useRef(0);

  const fetchNoCache = async (url, signal) => {
    const res = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache', ...getTenantHeaders() },
      signal
    });
    return res;
  };

  const fetchData = useCallback(async () => {
    try {
      const API = getApiBase();
      const ts = Date.now();
      const taxReportRequestId = ++taxReportRequestRef.current;
      const expenseChartRequestId = ++expenseChartRequestRef.current;
      const appointmentCountRequestId = ++appointmentCountRequestRef.current;

      const loadArray = async (url, setRows) => {
        const res = await fetchNoCache(url);
        if (res.ok) {
          const data = await res.json();
          setRows(Array.isArray(data) ? data : []);
        } else {
          setRows([]);
        }
      };

      void (async () => {
        try {
          const bizRes = await fetchNoCache(`${API}/api/settings/business?ts=${ts}`);
          if (bizRes.ok) {
            const bizData = await bizRes.json();
            if (bizData?.businessName) setBusinessName(String(bizData.businessName).trim());
          }
        } catch (e) {
          console.error("business name fetch failed", e);
        }
      })();

      void (async () => {
        try {
          const taxReportRes = await fetchNoCache(`${API}/api/taxcenter/report?year=${selectedYear}&ts=${ts}`);
          if (taxReportRes.ok) {
            const taxReportData = await taxReportRes.json();
            if (taxReportRequestId !== taxReportRequestRef.current) return;
            setTaxCenterReport(taxReportData && typeof taxReportData === 'object' ? taxReportData : null);
          } else {
            if (taxReportRequestId !== taxReportRequestRef.current) return;
            setTaxCenterReport(null);
          }
        } catch (e) {
          console.error('tax center report fetch failed', e);
          if (taxReportRequestId !== taxReportRequestRef.current) return;
          setTaxCenterReport(null);
        }
      })();

      void loadArray(`${API}/api/invoices?ts=${ts}`, setInvoices)
        .catch((e) => console.error('Dashboard data fetch failed:', e));

      void (async () => {
        try {
          const expenseChartRes = await fetchNoCache(`${API}/api/expenses/dashboard-monthly?year=${selectedYear}&ts=${ts}`);
          if (expenseChartRes.ok) {
            const rows = await expenseChartRes.json();
            if (expenseChartRequestId !== expenseChartRequestRef.current) return;
            const months = Array(12).fill(0);
            (Array.isArray(rows) ? rows : []).forEach((row) => {
              const month = Number(row?.month);
              const value = Number(row?.value || 0);
              if (month >= 1 && month <= 12 && Number.isFinite(value)) {
                months[month - 1] = value;
              }
            });
            setMonthlyExpenses(months);
          } else if (expenseChartRequestId === expenseChartRequestRef.current) {
            setMonthlyExpenses(Array(12).fill(0));
          }
        } catch (e) {
          console.error('Dashboard monthly expenses fetch failed:', e);
          if (expenseChartRequestId !== expenseChartRequestRef.current) return;
          setMonthlyExpenses(Array(12).fill(0));
        }
      })();

      void loadArray(`${API}/api/estimates?ts=${ts}`, setEstimates)
        .catch((e) => console.error('Dashboard data fetch failed:', e));

      void (async () => {
        const inventoryAlertsRequestId = ++inventoryAlertsRequestRef.current;
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 15000);

        try {
          const invAlertRes = await fetchNoCache(
            `${API}/api/inventory?ts=${ts}`,
            controller.signal
          );
          if (!invAlertRes.ok) {
            throw new Error(`Inventory alerts request failed (${invAlertRes.status}).`);
          }

          const invAlertData = await invAlertRes.json();
          if (!Array.isArray(invAlertData)) {
            throw new Error('Inventory alerts returned an invalid response.');
          }
          if (inventoryAlertsRequestId !== inventoryAlertsRequestRef.current) return;

          setInventoryValue(invAlertData.reduce(
            (sum, item) => sum + (Number(item.qtyOnHand || 0) * Number(item.cost || 0)),
            0
          ));
          setInventoryAlerts(
            invAlertData.filter(item => {
              if (item.reorderPoint == null) return false;
              const qty = Number(item.qtyOnHand ?? 0);
              const reorder = Number(item.reorderPoint);
              return qty <= reorder;
            })
          );
          setInventoryAlertsError(false);
        } catch (e) {
          if (inventoryAlertsRequestId !== inventoryAlertsRequestRef.current) return;
          console.error('inventory alerts fetch failed', e);
          setInventoryAlertsError(true);
        } finally {
          window.clearTimeout(timeout);
        }
      })();

      void (async () => {
        try {
          const today = toLocalYMD(new Date());
          const appointmentCountRes = await fetchNoCache(
            `${API}/api/appointments/today-count?date=${encodeURIComponent(today)}&ts=${ts}`
          );
          if (!appointmentCountRes.ok) {
            throw new Error(`Appointment count request failed (${appointmentCountRes.status}).`);
          }

          const data = await appointmentCountRes.json();
          const count = Number(data?.count);
          if (!Number.isInteger(count) || count < 0) {
            throw new Error('Appointment count returned an invalid response.');
          }
          if (appointmentCountRequestId !== appointmentCountRequestRef.current) return;
          setAppointmentsToday(count);
        } catch (e) {
          console.error('appointment count fetch failed', e);
          if (appointmentCountRequestId !== appointmentCountRequestRef.current) return;
          setAppointmentsToday(0);
        }
      })();
    } catch (e) {
      console.error('Dashboard data fetch failed:', e);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (localStorage.getItem('onboarding_dismissed') === 'true') { return; }
    const remindAt = localStorage.getItem('onboarding_remind_at');
    if (remindAt && Date.now() < Number(remindAt)) {
      return;
    }
    const token = localStorage.getItem('auth_token');
    if (!token) { return; }
    fetch(`${getApiBase()}/api/auth/onboarding-status`, {
      headers: { 'Authorization': `Bearer ${token}`, ...getTenantHeaders() }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.needsSetup) setShowOnboarding(true);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let timer;
    const scheduleMidnightUpdate = () => {
      const now = new Date();
      const nextMidnight = new Date(now);
      nextMidnight.setHours(24, 0, 1, 0);
      timer = window.setTimeout(() => {
        setDashboardToday(new Date());
        scheduleMidnightUpdate();
      }, nextMidnight.getTime() - now.getTime());
    };
    scheduleMidnightUpdate();
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const onFocus = () => fetchData();
    const onVis = () => { if (!document.hidden) fetchData(); };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fetchData]);

  const inYear = useCallback((dtAny) => {
    const d = parseDate(dtAny);
    if (!d) return false;
    return d.getFullYear() === Number(selectedYear);
  }, [selectedYear]);

  const revenueYTD = useMemo(() => {
    return (invoices || []).reduce((acc, inv) => {
      if (!inYear(getCreatedAt(inv))) return acc;
      return acc + (Number(inv.total || 0) - Number(inv.stateTaxAmount || 0) - Number(inv.countyTaxAmount || 0));
    }, 0);
  }, [invoices, inYear]);

  const taxCenterPerformance = taxCenterReport?.performance || {};
  const taxCenterMileage = taxCenterReport?.mileage || {};
  const estimatedNetProfitAfterTaxes = Number(taxCenterPerformance.trueNetProfit || 0);
  const totalExpenses = Number(taxCenterPerformance.totalExpenses || 0);
  const totalMileage = Number(taxCenterMileage.totalMiles || 0);
  const mileageDeduction = Number(taxCenterMileage.mileageDeduction || 0);
  const rawMileageRate = taxCenterMileage.mileageRate;
  const mileageRate = rawMileageRate == null ? null : Number(rawMileageRate);
  const multipleMileageRates = taxCenterMileage.multipleRatesApplied === true;

  const overdueInfo = useMemo(() => {
    let count = 0;
    let dollars = 0;

    (invoices || []).forEach(inv => {
      if (isInvoiceOverdue(inv)) {
        count += 1;
        const total = Number(inv.total || 0);
        const paid = Number(inv.amount_paid || inv.paid_amount || 0);
        const balance = Number(inv.balance_due ?? Math.max(0, total - paid));
        dollars += balance;
      }
    });

    return { count, dollars };
  }, [invoices]);

  const pendingEstimatesCount = useMemo(() => {
    const list = Array.isArray(estimates) ? estimates : [];
    let count = 0;

    list.forEach(e => {
      if (isEstimatePending(e)) count += 1;
    });

    return count;
  }, [estimates]);

  const pendingEstimatesTotal = useMemo(() => {
    const list = Array.isArray(estimates) ? estimates : [];
    let total = 0;
    list.forEach(e => {
      if (isEstimatePending(e)) total += Number(e.total ?? e.amount ?? e.estimate_total ?? 0);
    });
    return total;
  }, [estimates]);

  const styles = {
    container: {
      padding: isMobile ? '15px' : '40px',
      backgroundColor: '#E8E2DA',
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      boxSizing: 'border-box'
    },
    headerLine: {
      border: '8px solid black',
      padding: isMobile ? '12px' : '16px',
      marginBottom: '20px',
      backgroundColor: '#dbe8f7',
      color: 'black',
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
      gap: '10px',
      alignItems: 'center',
      boxShadow: 'inset 0 0 0 3px #39FF14, inset 0 0 0 6px #2aa8ff'
    },
    headerLeft: { fontWeight: 900, textTransform: 'uppercase', fontSize: isMobile ? '18px' : '22px' },
    headerCenter: { fontWeight: 900, textTransform: 'uppercase', textAlign: 'center', fontSize: isMobile ? '20px' : '26px', color: '#000000' },
    headerRight: { display: 'flex', gap: '10px', justifyContent: isMobile ? 'flex-start' : 'flex-end', flexWrap: 'wrap' },
    select: { padding: '12px', border: '4px solid black', fontWeight: 900, backgroundColor: '#e0e0e0', color: 'black' },
    statGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
      gap: isMobile ? '10px' : '20px',
      width: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
      alignItems: 'stretch',
      gridAutoRows: isMobile ? '175px' : '235px'
    },
    statBox: {
      border: '6px solid black',
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      boxSizing: 'border-box',
      overflow: 'hidden',
      minHeight: isMobile ? '175px' : '235px',
      height: isMobile ? '175px' : '235px',
      borderRadius: 0
    },
    overdueBox: {
      border: '6px solid black',
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      boxSizing: 'border-box',
      overflow: 'hidden',
      minHeight: isMobile ? '175px' : '235px',
      height: isMobile ? '175px' : '235px',
      borderRadius: 0
    },
    statHeader: {
      position: 'relative',
      height: isMobile ? '60px' : '75px',
      flex: isMobile ? '0 0 60px' : '0 0 75px',
      padding: isMobile ? '4px 6px' : '12px',
      background: 'linear-gradient(145deg, #006b57 0%, #003d33 100%)',
      borderBottom: '5px solid black',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box'
    },
    overdueHeader: {
      position: 'relative',
      height: isMobile ? '60px' : '75px',
      flex: isMobile ? '0 0 60px' : '0 0 75px',
      padding: isMobile ? '4px 6px' : '12px',
      background: 'linear-gradient(145deg, #d71920 0%, #8f0d14 100%)',
      borderBottom: '5px solid black',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box'
    },
    statHeaderContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: isMobile ? '6px' : '8px',
      width: '100%',
      minWidth: 0
    },
    statIcon: {
      width: isMobile ? '30px' : '34px',
      height: isMobile ? '30px' : '34px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: '0 0 auto',
      filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.45))'
    },
    statLabel: {
      fontSize: isMobile ? '17px' : '18px',
      fontWeight: '900',
      textTransform: 'uppercase',
      color: 'white',
      textAlign: 'center',
      lineHeight: 1.12,
      width: '100%',
      flex: '1 1 auto',
      minWidth: 0,
      whiteSpace: 'nowrap',
      overflowWrap: 'normal',
      wordBreak: 'normal',
      textShadow: '0 2px 2px rgba(0,0,0,0.45)'
    },
    statTitleGroup: {
      display: 'flex',
      flex: '1 1 auto',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      minWidth: 0
    },
    statSubtitle: {
      marginTop: isMobile ? '1px' : '3px',
      color: 'white',
      fontSize: isMobile ? '9px' : '13px',
      fontWeight: '800',
      lineHeight: 1,
      textAlign: 'center',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      textShadow: '0 2px 2px rgba(0,0,0,0.45)'
    },
    statNotch: {
      position: 'absolute',
      left: '50%',
      bottom: isMobile ? '-19px' : '-24px',
      transform: 'translateX(-50%)',
      width: 0,
      height: 0,
      borderLeft: isMobile ? '24px solid transparent' : '30px solid transparent',
      borderRight: isMobile ? '24px solid transparent' : '30px solid transparent',
      borderTop: isMobile ? '19px solid black' : '24px solid black',
      zIndex: 2
    },
    statNotchInner: {
      position: 'absolute',
      left: isMobile ? '-17px' : '-22px',
      top: isMobile ? '-19px' : '-24px',
      width: 0,
      height: 0,
      borderLeft: isMobile ? '17px solid transparent' : '22px solid transparent',
      borderRight: isMobile ? '17px solid transparent' : '22px solid transparent',
      borderTop: isMobile ? '13px solid #004d40' : '17px solid #004d40'
    },
    overdueNotchInner: {
      position: 'absolute',
      left: isMobile ? '-17px' : '-22px',
      top: isMobile ? '-19px' : '-24px',
      width: 0,
      height: 0,
      borderLeft: isMobile ? '17px solid transparent' : '22px solid transparent',
      borderRight: isMobile ? '17px solid transparent' : '22px solid transparent',
      borderTop: isMobile ? '13px solid #aa1118' : '17px solid #aa1118'
    },
    statBody: {
      flex: '1 1 auto',
      padding: isMobile ? '18px 8px 4px' : '24px 16px 6px',
      background: 'linear-gradient(145deg, #07906e 0%, #005443 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 0,
      boxSizing: 'border-box'
    },
    overdueBody: {
      flex: '1 1 auto',
      padding: isMobile ? '18px 8px 4px' : '24px 16px 6px',
      background: 'linear-gradient(145deg, #ff4b50 0%, #c7131b 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 0,
      boxSizing: 'border-box'
    },
    statValue: {
      fontSize: isMobile ? '42px' : '60px',
      fontWeight: '900',
      color: '#E8C98F',
      lineHeight: 1.05,
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      whiteSpace: 'nowrap',
      textAlign: 'center',
      textShadow: '0 2px 3px rgba(0,0,0,0.35)'
    },
    smallWhite: {
      fontSize: isMobile ? '13px' : '16px',
      marginTop: isMobile ? '2px' : '4px',
      fontWeight: '900',
      color: 'white',
      textAlign: 'center',
      lineHeight: 1.25,
      maxWidth: '100%',
      minWidth: 0,
      overflowWrap: 'normal',
      wordBreak: 'normal',
      textShadow: '0 2px 2px rgba(0,0,0,0.45)'
    }
  };

  return (
    <div className="jfn-dashboard-page" style={styles.container}>
      <style>{`
        @media (max-width: 767px) {
          .jfn-dashboard-page {
            padding-left: 6px !important;
            padding-right: 6px !important;
          }
        }
        @keyframes dashboardAppointmentCountPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.70; }
        }
        .dashboard-appointment-count-alert {
          animation: dashboardAppointmentCountPulse 1.2s ease-in-out infinite;
        }
        .mtn-dashboard-top-row {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 16px;
          margin-bottom: 20px;
        }
        .mtn-dashboard-top-panel {
          position: relative;
          min-height: 98px;
          padding: 14px 22px;
          overflow: hidden;
          border: 6px solid #121516;
          outline: 2px solid #f07d00;
          outline-offset: -4px;
          background:
            radial-gradient(circle at 13px 13px, #f6f2dc 0 2px, #7b7f79 2.5px 4px, #121415 4.5px 7px, transparent 7.5px),
            radial-gradient(circle at calc(100% - 13px) 13px, #f6f2dc 0 2px, #7b7f79 2.5px 4px, #121415 4.5px 7px, transparent 7.5px),
            radial-gradient(circle at 13px calc(100% - 13px), #f6f2dc 0 2px, #7b7f79 2.5px 4px, #121415 4.5px 7px, transparent 7.5px),
            radial-gradient(circle at calc(100% - 13px) calc(100% - 13px), #f6f2dc 0 2px, #7b7f79 2.5px 4px, #121415 4.5px 7px, transparent 7.5px),
            linear-gradient(145deg, rgba(68, 74, 73, .78), rgba(9, 12, 13, .84) 46%, rgba(39, 45, 45, .78)),
            url('/MTN-WEATHERED-CAST-IRON.png');
          background-size: auto, auto, auto, auto, 100% 100%, cover;
          background-position: center;
          box-shadow: 0 0 13px rgba(255,116,0,.72), inset 0 0 0 2px #7c8584, inset 0 0 0 5px #050606, inset 0 0 15px #000;
        }
        .mtn-dashboard-title-panel { grid-column: 1 / -1; min-height: 76px; display: grid; grid-template-columns: auto minmax(0, 1fr) auto auto; align-items: center; gap: 14px; text-align: left; }
        .mtn-dashboard-title { color: #f2f3ec; font-size: clamp(29px, 3vw, 43px); font-weight: 900; line-height: .95; letter-spacing: 1px; text-shadow: 0 2px 0 #666, 0 4px 5px #000; }
        .mtn-dashboard-subtitle { color: #fff0ba; font-size: clamp(13px, 1.4vw, 19px); font-style: italic; font-weight: 800; text-shadow: 0 2px 2px #000; min-width: 0; }
        .mtn-dashboard-year { margin: 0; padding: 5px 9px; border: 2px solid #ff9800; background: #090b0c; color: #ffd34e; font-weight: 900; }
        .mtn-dashboard-sound-toggle { margin: 0; min-height: 30px; padding: 4px 8px; border: 2px solid #ff9800; background: #090b0c; color: #ffd34e; font: 900 12px/1 Arial, Helvetica, sans-serif; cursor: pointer; white-space: nowrap; }
        .mtn-dashboard-sound-toggle[aria-pressed='true'] { border-color: #ffea00; color: #fff3a2; box-shadow: 0 0 8px rgba(255,151,0,.7); }
        .mtn-appointments-panel { display: flex; align-items: center; justify-content: center; gap: 15px; cursor: pointer; font: inherit; color: inherit; text-align: center; }
        .mtn-appointments-panel:hover { outline-color: #ffea00; box-shadow: 0 0 20px rgba(255,151,0,.95), inset 0 0 0 2px #7c8584, inset 0 0 0 5px #050606, inset 0 0 15px #000; }
        .mtn-appointments-copy { min-width: 0; display: flex; flex-direction: column; align-items: center; gap: 7px; text-align: center; }
        .mtn-appointments-label { color: #ffcf2c; font-size: clamp(18px, 2vw, 28px); font-weight: 900; line-height: 1; text-transform: uppercase; text-shadow: 0 2px 2px #000, 0 0 6px rgba(255,150,0,.45); }
        .mtn-appointments-detail { margin-top: 8px; color: #fff3ca; font-size: clamp(12px, 1.3vw, 17px); font-weight: 800; }
        .mtn-appointments-count { display: flex; align-items: center; justify-content: center; flex: 0 0 auto; min-width: 62px; min-height: 62px; padding: 5px; border: 3px solid #ff8f00; border-radius: 12px; background: linear-gradient(145deg, #38170a, #080909); color: #ffd13a; font-size: clamp(38px, 4.3vw, 59px); font-weight: 900; line-height: 1; text-shadow: 0 3px 2px #000; box-shadow: inset 0 2px 0 rgba(255,220,120,.3), 0 0 12px rgba(255,120,0,.6); }
        .mtn-date-panel { display: flex; align-items: center; justify-content: center; text-align: center; }
        .mtn-date-value { color: #fff; font-size: clamp(16px, 1.8vw, 25px); font-weight: 900; line-height: 1.15; text-shadow: 0 2px 2px #000; }
        .mtn-date-holiday { margin-top: 8px; color: #fff0ba; font-size: clamp(13px, 1.35vw, 18px); font-style: italic; font-weight: 800; text-shadow: 0 2px 2px #000; }
        @media (max-width: 1023px) {
          .mtn-dashboard-top-row { grid-template-columns: 1fr; gap: 10px; margin-bottom: 10px; }
          .mtn-dashboard-title-panel { grid-column: auto; }
          .mtn-dashboard-top-panel { min-height: 88px; }
          .mtn-appointments-panel { min-height: 98px; }
        }
        @media (max-width: 480px) {
          .mtn-dashboard-top-panel { padding: 12px 14px; }
          .mtn-appointments-panel { gap: 9px; }
          .mtn-appointments-count { min-width: 52px; min-height: 52px; }
        }
        @media (max-width: 768px) {
          .mtn-dashboard-top-panel { min-height: 76px; padding: 9px 12px; }
          .mtn-dashboard-title-panel { grid-template-columns: repeat(2, minmax(0, 1fr)); grid-template-areas: 'title title' 'subtitle subtitle' 'year sound'; gap: 6px 10px; }
          .mtn-dashboard-title { grid-area: title; font-size: clamp(23px, 7vw, 32px); text-align: center; }
          .mtn-dashboard-subtitle { grid-area: subtitle; font-size: 11px; line-height: 1.1; text-align: center; }
          .mtn-dashboard-year { grid-area: year; width: 100%; box-sizing: border-box; padding: 4px 7px; text-align: center; }
          .mtn-dashboard-sound-toggle { grid-area: sound; width: 100%; box-sizing: border-box; }
          .mtn-appointments-panel { min-height: 86px; gap: 8px; }
          .mtn-appointments-copy { gap: 4px; }
          .mtn-appointments-label { font-size: clamp(16px, 5vw, 22px); line-height: 1.05; }
          .mtn-appointments-detail { margin-top: 0; font-size: 12px; }
          .mtn-appointments-count { min-width: 48px; min-height: 48px; font-size: 36px; }
          .mtn-dashboard-sound-toggle { min-height: 28px !important; padding: 4px 7px !important; font-size: 11px !important; }
        }
        .mtn-kpi-card {
          --mtn-kpi-bright: #ff9800;
          --mtn-kpi-mid: #b94700;
          --mtn-kpi-deep: #4a1800;
          --mtn-kpi-header: #ffb11a;
          --mtn-kpi-body: #8b3000;
          position: relative !important;
          isolation: isolate;
          border: 6px solid #121516 !important;
          outline: 2px solid var(--mtn-kpi-bright);
          outline-offset: -4px;
          background:
            radial-gradient(circle at 14px 14px, #f6f2dc 0 2px, #7b7f79 2.5px 4px, #121415 4.5px 7px, transparent 7.5px),
            radial-gradient(circle at calc(100% - 14px) 14px, #f6f2dc 0 2px, #7b7f79 2.5px 4px, #121415 4.5px 7px, transparent 7.5px),
            radial-gradient(circle at 14px calc(100% - 14px), #f6f2dc 0 2px, #7b7f79 2.5px 4px, #121415 4.5px 7px, transparent 7.5px),
            radial-gradient(circle at calc(100% - 14px) calc(100% - 14px), #f6f2dc 0 2px, #7b7f79 2.5px 4px, #121415 4.5px 7px, transparent 7.5px),
            linear-gradient(145deg, rgba(75, 80, 80, .78), rgba(18, 21, 22, .86) 42%, rgba(42, 47, 46, .78)),
            url('/MTN-WEATHERED-CAST-IRON.png');
          background-size: auto, auto, auto, auto, 100% 100%, cover;
          background-position: center;
          box-shadow: 0 0 12px var(--mtn-kpi-bright), inset 0 0 0 2px #777f7e, inset 0 0 0 5px #050606, inset 0 0 16px #000 !important;
        }
        .mtn-kpi-card > div:first-child {
          background: linear-gradient(145deg, var(--mtn-kpi-header), var(--mtn-kpi-mid) 48%, var(--mtn-kpi-deep)), url('/MTN-WEATHERED-CAST-IRON.png') !important;
          background-size: 100% 100%, cover !important;
          background-position: center !important;
          background-blend-mode: normal, soft-light !important;
          border-bottom: 5px solid #080909 !important;
          box-shadow: inset 0 2px 0 rgba(255,255,255,.38), inset 0 -3px 0 rgba(0,0,0,.5), 0 3px 0 #070808 !important;
        }
        .mtn-kpi-card > div:nth-child(2) {
          background:
            radial-gradient(ellipse at 24% 26%, rgba(255,255,255,.18) 0 1px, transparent 2px),
            radial-gradient(ellipse at 70% 70%, rgba(0,0,0,.42) 0 1px, transparent 2px),
            linear-gradient(145deg, var(--mtn-kpi-body), var(--mtn-kpi-deep)),
            url('/MTN-WEATHERED-CAST-IRON.png') !important;
          background-size: auto, auto, 100% 100%, cover !important;
          background-position: center !important;
          background-blend-mode: normal, normal, normal, soft-light !important;
          box-shadow: inset 0 2px 0 rgba(255,255,255,.16), inset 0 0 18px rgba(0,0,0,.55) !important;
        }
        .mtn-kpi-card > div:first-child,
        .mtn-kpi-card > div:nth-child(2) { position: relative; isolation: isolate; }
        .mtn-kpi-card > div:first-child::before,
        .mtn-kpi-card > div:nth-child(2)::before {
          content: '';
          position: absolute;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          opacity: .42;
          background: url('/MTN-WEATHERED-CAST-IRON.png') center / cover;
          mix-blend-mode: overlay;
        }
        .mtn-kpi-card > div:first-child > *,
        .mtn-kpi-card > div:nth-child(2) > * { position: relative; z-index: 1; }
        .mtn-kpi-card > div:first-child > span[aria-hidden="true"] { border-top-color: #080909 !important; }
        .mtn-kpi-card > div:first-child > span[aria-hidden="true"] > span { border-top-color: var(--mtn-kpi-mid) !important; }
        .mtn-kpi-card .mtn-kpi-value {
          color: #ffd34e !important;
          text-shadow: 0 2px 0 #5c2100, 0 4px 5px #000, 0 0 8px rgba(255,184,0,.35) !important;
        }
        .mtn-kpi-card .mtn-kpi-detail { text-shadow: 0 2px 2px #000, 0 0 4px rgba(255,255,255,.22) !important; }
        .mtn-kpi-orange { --mtn-kpi-bright: #ff9800; --mtn-kpi-header: #ffc943; --mtn-kpi-mid: #b84700; --mtn-kpi-body: #842a00; --mtn-kpi-deep: #4b1800; }
        .mtn-kpi-teal { --mtn-kpi-bright: #20e0d0; --mtn-kpi-header: #48e8d6; --mtn-kpi-mid: #087c78; --mtn-kpi-body: #086360; --mtn-kpi-deep: #04393d; }
        .mtn-kpi-red { --mtn-kpi-bright: #ff3535; --mtn-kpi-header: #ff6661; --mtn-kpi-mid: #a90c18; --mtn-kpi-body: #7e0a13; --mtn-kpi-deep: #4c0309; }
        .mtn-kpi-burgundy { --mtn-kpi-bright: #df7580; --mtn-kpi-header: #e8939b; --mtn-kpi-mid: #7a2637; --mtn-kpi-body: #5c1d2b; --mtn-kpi-deep: #310d18; }
        .mtn-kpi-blue { --mtn-kpi-bright: #31b8ff; --mtn-kpi-header: #60caff; --mtn-kpi-mid: #0875b7; --mtn-kpi-body: #075a92; --mtn-kpi-deep: #06335d; }
        .mtn-kpi-green { --mtn-kpi-bright: #9ded24; --mtn-kpi-header: #b7f143; --mtn-kpi-mid: #4e8312; --mtn-kpi-body: #3d6911; --mtn-kpi-deep: #1f410b; }
        .mtn-kpi-blue { order: 1; }
        .mtn-kpi-burgundy { order: 2; }
        .mtn-kpi-green { order: 3; }
        @media (prefers-reduced-motion: reduce) {
          .dashboard-appointment-count-alert {
            animation: none;
          }
        }
      `}</style>
      {showOnboarding && (
        <OnboardingPopup
          navigate={navigate}
          onDismiss={() => {
            localStorage.setItem('onboarding_dismissed', 'true');
            setShowOnboarding(false);
          }}
          onRemindTomorrow={() => {
            localStorage.setItem('onboarding_remind_at', String(Date.now() + 24 * 60 * 60 * 1000));
            setShowOnboarding(false);
          }}
        />
      )}
      <div className="mtn-dashboard-top-row">
        <section className="mtn-dashboard-top-panel mtn-dashboard-title-panel" aria-label="Dashboard title">
          <div className="mtn-dashboard-title">DASHBOARD</div>
          <div className="mtn-dashboard-subtitle"><span className="mtn-dashboard-business-name">{businessName} • </span>Your Business at a Glance</div>
          <select
            className="mtn-dashboard-year"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            title="Select Year"
          >
            {Array.from(
              { length: 10 },
              (_, i) => currentYear - 7 + i
            ).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            className="mtn-dashboard-sound-toggle"
            type="button"
            aria-pressed={soundEnabled}
            onClick={() => onSoundEnabledChange((enabled) => !enabled)}
          >
            {soundEnabled ? '🔊 SOUND ON' : '🔇 SOUND OFF'}
          </button>
        </section>

        <button className="mtn-dashboard-top-panel mtn-appointments-panel" type="button" onClick={() => navigate('calendar')}>
          <span className="mtn-appointments-copy">
            <span className="mtn-appointments-label">Appointments Today</span>
            <span className="mtn-appointments-detail">View Scheduler</span>
          </span>
          <span
            className={`mtn-appointments-count${appointmentsToday > 0 ? ' dashboard-appointment-count-alert' : ''}`}
            style={{ color: appointmentsToday > 0 ? '#FFFF00' : 'rgba(255,255,255,0.78)' }}
          >
            {appointmentsToday}
          </span>
        </button>

        <section className="mtn-dashboard-top-panel mtn-date-panel" aria-label="Current date and holiday">
          <div>
            <div className="mtn-date-value">{formatDashboardDate(dashboardToday)}</div>
            {getDashboardHoliday(dashboardToday) ? <div className="mtn-date-holiday">{getDashboardHoliday(dashboardToday)}</div> : null}
          </div>
        </section>
      </div>

      <div style={styles.statGrid}>
        <div className="mtn-kpi-card mtn-kpi-orange" style={styles.statBox}>
          <div style={styles.statHeader}>
            <div style={styles.statHeaderContent}>
              <span style={styles.statIcon}>
                <StatCardIcon type="invoice" size={isMobile ? 30 : 34} />
              </span>
              <AutoFitText style={styles.statLabel} maxFontSize={isMobile ? 17 : 18}>
                TOTAL INVOICED YTD
              </AutoFitText>
            </div>
            <span aria-hidden="true" style={styles.statNotch}>
              <span style={styles.statNotchInner} />
            </span>
          </div>
          <div style={styles.statBody}>
            <AutoFitText
              style={{ ...styles.statValue, color: '#ffd34e', textShadow: '0 2px 0 #5c2100, 0 4px 5px #000, 0 0 8px rgba(255,184,0,.35)' }}
              maxFontSize={isMobile ? 42 : 60}
            >
              {money(revenueYTD)}
            </AutoFitText>
            <div className="mtn-kpi-detail" style={styles.smallWhite}>(PAID & UNPAID)</div>
          </div>
        </div>

        <div className="mtn-kpi-card mtn-kpi-teal" style={styles.statBox}>
          <div style={styles.statHeader}>
            <div style={styles.statHeaderContent}>
              <span style={styles.statIcon}>
                <StatCardIcon type="profit" size={isMobile ? 30 : 34} />
              </span>
              <div style={isMobile ? styles.statTitleGroup : { ...styles.statTitleGroup, gap: '3px' }}>
                {isMobile ? (
                  <>
                    <AutoFitText style={styles.statLabel} maxFontSize={17}>
                      ESTIMATED NET PROFIT AFTER
                    </AutoFitText>
                    <div style={styles.statSubtitle}>
                      (Expenses, Mileage, Taxes)
                    </div>
                  </>
                ) : (
                  <>
                    <AutoFitText style={{ ...styles.statLabel, lineHeight: 1 }} maxFontSize={16}>
                      ESTIMATED
                    </AutoFitText>
                    <AutoFitText style={{ ...styles.statLabel, fontSize: '14px', lineHeight: 1 }} maxFontSize={14}>
                      NET PROFIT AFTER
                    </AutoFitText>
                    <div style={{ ...styles.statSubtitle, marginTop: 0, fontSize: '10px' }}>
                      (Expenses, Mileage, Taxes)
                    </div>
                  </>
                )}
              </div>
            </div>
            <span aria-hidden="true" style={styles.statNotch}>
              <span style={styles.statNotchInner} />
            </span>
          </div>
          <div style={styles.statBody}>
            <AutoFitText
              style={{ ...styles.statValue, color: '#ffd34e', textShadow: '0 2px 0 #5c2100, 0 4px 5px #000, 0 0 8px rgba(255,184,0,.35)' }}
              maxFontSize={isMobile ? 42 : 60}
            >
              {money(estimatedNetProfitAfterTaxes)}
            </AutoFitText>
          </div>
        </div>

        <div className="mtn-kpi-card mtn-kpi-red" style={styles.overdueBox}>
          <div style={styles.overdueHeader}>
            <div
              className={overdueInfo.count > 0 ? 'dashboard-appointment-count-alert' : undefined}
              style={styles.statHeaderContent}
            >
              <span style={styles.statIcon}>
                <StatCardIcon type="overdue" size={isMobile ? 30 : 34} />
              </span>
              <AutoFitText style={styles.statLabel} maxFontSize={isMobile ? 17 : 18}>
                OVERDUE INVOICES
              </AutoFitText>
            </div>
            <span aria-hidden="true" style={styles.statNotch}>
              <span style={styles.overdueNotchInner} />
            </span>
          </div>
          <div style={styles.overdueBody}>
            <div
              className={overdueInfo.count > 0 ? 'dashboard-appointment-count-alert' : undefined}
              style={{ width: '100%', minWidth: 0, textAlign: 'center' }}
            >
              <div className="mtn-kpi-value" style={{ ...styles.statValue, color: '#E8C98F' }}>
                {overdueInfo.count}
              </div>
              <div className="mtn-kpi-detail" style={styles.smallWhite}>TOTAL OVERDUE: {money(overdueInfo.dollars)}</div>
            </div>
          </div>
        </div>

        <div className="mtn-kpi-card mtn-kpi-burgundy" style={styles.statBox}>
          <div style={styles.statHeader}>
            <div style={styles.statHeaderContent}>
              <span style={styles.statIcon}>
                <StatCardIcon type="expenses" size={isMobile ? 30 : 34} />
              </span>
              <AutoFitText style={styles.statLabel} maxFontSize={isMobile ? 17 : 18}>
                TOTAL EXPENSES
              </AutoFitText>
            </div>
            <span aria-hidden="true" style={styles.statNotch}>
              <span style={styles.statNotchInner} />
            </span>
          </div>
          <div style={styles.statBody}>
            <AutoFitText
              style={{ ...styles.statValue, color: '#ffd34e', textShadow: '0 2px 0 #5c2100, 0 4px 5px #000, 0 0 8px rgba(255,184,0,.35)' }}
              maxFontSize={isMobile ? 42 : 60}
            >
              {money(totalExpenses)}
            </AutoFitText>
          </div>
        </div>

        <div className="mtn-kpi-card mtn-kpi-blue" style={styles.statBox}>
          <div style={styles.statHeader}>
            <div style={styles.statHeaderContent}>
              <span style={styles.statIcon}>
                <StatCardIcon type="mileage" size={isMobile ? 30 : 34} />
              </span>
              <AutoFitText style={styles.statLabel} maxFontSize={isMobile ? 17 : 18}>
                TOTAL MILEAGE
              </AutoFitText>
            </div>
            <span aria-hidden="true" style={styles.statNotch}>
              <span style={styles.statNotchInner} />
            </span>
          </div>
          <div style={styles.statBody}>
            <div className="mtn-kpi-value" style={styles.statValue}>{Number(totalMileage || 0).toFixed(0)}</div>
            <div className="mtn-kpi-detail" style={styles.smallWhite}>DEDUCTION: {money(mileageDeduction)}</div>
            {Number.isFinite(mileageRate) ? (
              <div className="mtn-kpi-detail" style={styles.smallWhite}>IRS Rate: ${mileageRate.toFixed(3)}/mile</div>
            ) : multipleMileageRates ? (
              <div className="mtn-kpi-detail" style={styles.smallWhite}>Multiple IRS Rates Applied</div>
            ) : null}
          </div>
        </div>

        <div className="mtn-kpi-card mtn-kpi-green" style={styles.statBox}>
          <div style={styles.statHeader}>
            <div style={styles.statHeaderContent}>
              <span style={styles.statIcon}>
                <StatCardIcon type="estimates" size={isMobile ? 30 : 34} />
              </span>
              <AutoFitText style={styles.statLabel} maxFontSize={isMobile ? 17 : 18}>
                PENDING ESTIMATES
              </AutoFitText>
            </div>
            <span aria-hidden="true" style={styles.statNotch}>
              <span style={styles.statNotchInner} />
            </span>
          </div>
          <div style={styles.statBody}>
            <div
              className={`mtn-kpi-value${pendingEstimatesCount > 0 ? ' dashboard-appointment-count-alert' : ''}`}
              style={styles.statValue}
            >
              {pendingEstimatesCount}
            </div>
            <div className="mtn-kpi-detail" style={styles.smallWhite}>TOTAL VALUE: {money(pendingEstimatesTotal)}</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: isMobile ? 10 : 20, marginTop: isMobile ? 10 : 20, alignItems: 'stretch' }}>
      {/* MONTHLY REVENUE BAR CHART */}
      {(() => {
        const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
        const monthlyIncomingRevenue = taxCenterReport?.performance?.monthlyIncomingRevenue;
        const revenueData = MONTHS.map((label, i) => {
          const row = Array.isArray(monthlyIncomingRevenue)
            ? monthlyIncomingRevenue.find((item) => Number(item.month) === i + 1)
            : null;

          return { month: label, income: Number(row?.value || 0), expenses: Number(monthlyExpenses[i] || 0) };
        });
        return (
          <div className="mtn-industrial-panel mtn-chart-panel" style={{
            border: '6px solid #15191a',
            outline: '2px solid #ff8c00',
            outlineOffset: '-4px',
            padding: isMobile ? '10px' : '24px',
            backgroundImage: "linear-gradient(145deg, rgba(61, 68, 67, .80), rgba(9, 11, 12, .90) 46%, rgba(34, 40, 40, .80)), url('/MTN-WEATHERED-CAST-IRON.png')",
            backgroundSize: '100% 100%, cover',
            backgroundPosition: 'center',
            boxShadow: '0 0 14px rgba(255,120,0,.68), inset 0 0 0 2px #78807d, inset 0 0 0 5px #050606, inset 0 0 18px #000'
          }}>
            <div style={{
              fontWeight: 900,
              fontSize: isMobile ? 13 : 16,
              color: '#ffd34e',
              textTransform: 'uppercase',
              letterSpacing: 2,
              marginBottom: isMobile ? 10 : 16,
              textAlign: 'center',
              borderBottom: '2px solid #ff8c00',
              paddingBottom: 8
            }}>
              📊 INCOME VS. EXPENSES (CASH BASIS) — {selectedYear}
            </div>
            <div ref={revChartRef} style={{ display: 'flex', alignItems: 'stretch', gap: isMobile ? 0 : 12, background: 'linear-gradient(145deg, #222827, #090b0c)', borderRadius: 4, padding: '8px 0', overflowX: isMobile ? 'hidden' : 'auto', boxShadow: 'inset 0 0 0 2px #4e5754, inset 0 0 14px #000' }}>
              {!isMobile && (
                <div style={{ flex: '0 0 250px', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: 8, filter: 'drop-shadow(0 0 8px rgba(255, 110, 0, .55))' }}>
                  <img src="/MTN-ROBOT-CHART-POINTER.gif" alt="MyTool NOW chart pointer robot" style={{ display: 'block', width: 235, height: 'auto' }} />
                </div>
              )}
              <div style={{ flex: '1 1 auto', minWidth: isMobile ? 0 : 600 }}>
              <ResponsiveContainer width="100%" height={isMobile ? 210 : 300}>
                <BarChart data={revenueData} margin={isMobile ? { top: 4, right: 2, left: 0, bottom: 0 } : { top: 4, right: 8, left: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#fff09a" stopOpacity={1} />
                      <stop offset="28%" stopColor="#ffb000" stopOpacity={1} />
                      <stop offset="100%" stopColor="#f06400" stopOpacity={1} />
                    </linearGradient>
                    <linearGradient id="expensesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ff7a72" stopOpacity={1} />
                      <stop offset="35%" stopColor="#f02424" stopOpacity={1} />
                      <stop offset="100%" stopColor="#9b0711" stopOpacity={1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="#77807d" strokeDasharray="4 4" strokeOpacity={0.55} />
                  <XAxis dataKey="month" tick={{ fill: '#f5eadc', fontSize: isMobile ? 8 : 11, fontWeight: 900 }} axisLine={{ stroke: '#a4aaa4' }} tickLine={false} />
                  <YAxis tickFormatter={(v) => money(v).replace('.00', '')} tick={{ fill: '#f5eadc', fontSize: isMobile ? 8 : 11, fontWeight: 900 }} axisLine={false} tickLine={false} width={isMobile ? 42 : 60} />
                  <Tooltip
                    contentStyle={{ background: '#0a1128', border: '2px solid #ff9800', borderRadius: 6, color: '#fff', fontWeight: 900, fontSize: 13 }}
                    itemStyle={{ color: '#fff', fontWeight: 900 }}
                    labelStyle={{ color: '#ffd34e', fontWeight: 900, fontSize: 13 }}
                    cursor={{ fill: 'rgba(255,153,0,0.16)' }}
                    formatter={(value, name) => [money(value), name]}
                  />
                  <Bar dataKey="income" name="Payments Received (Income)" fill="url(#revenueGradient)" radius={[5, 5, 0, 0]} maxBarSize={isMobile ? 16 : 28} stroke="#e96b00" strokeWidth={3} activeBar={{ fill: '#ffbc18', stroke: '#fff0a0', strokeWidth: 3 }}>
                  </Bar>
                  <Bar dataKey="expenses" name="Expenses" fill="url(#expensesGradient)" radius={[5, 5, 0, 0]} maxBarSize={isMobile ? 16 : 28} stroke="#870913" strokeWidth={3} activeBar={{ fill: '#ff4b42', stroke: '#ffd0c8', strokeWidth: 3 }}>
                  </Bar>
                  <Legend verticalAlign="bottom" wrapperStyle={{ color: '#f5eadc', fontSize: isMobile ? 10 : 12, fontWeight: 900 }} />
                </BarChart>
              </ResponsiveContainer>
              </div>
            </div>
          </div>
        );
      })()}

      {/* INVENTORY REORDER ALERT */}
      <div className="mtn-industrial-panel mtn-inventory-panel" style={{
        border: '6px solid #15191a',
        outline: '2px solid #ff8c00',
        outlineOffset: '-4px',
        padding: isMobile ? '10px' : '20px',
        backgroundImage: "linear-gradient(145deg, rgba(61, 68, 67, .80), rgba(9, 11, 12, .90) 46%, rgba(34, 40, 40, .80)), url('/MTN-WEATHERED-CAST-IRON.png')",
        backgroundSize: '100% 100%, cover',
        backgroundPosition: 'center',
        boxShadow: '0 0 14px rgba(255,120,0,.68), inset 0 0 0 2px #78807d, inset 0 0 0 5px #050606, inset 0 0 18px #000'
      }}>
        <div className={inventoryAlerts?.length > 0 ? 'dashboard-appointment-count-alert' : undefined} style={{
          fontWeight: 900,
          fontSize: isMobile ? 13 : 16,
          color: '#ffd34e',
          textTransform: 'uppercase',
          letterSpacing: 2,
          marginBottom: 14,
          textAlign: 'center'
        }}>
          📦 INVENTORY REORDER ALERTS
          <div style={{ fontSize: isMobile ? 11 : 13, marginTop: 6 }}>
            INVENTORY VALUE: {money(inventoryValue)}
          </div>
        </div>
        {inventoryAlertsError ? (
          <div style={{ color: '#ff9800', fontWeight: 900, fontSize: isMobile ? 14 : 16, textAlign: 'center' }}>
            ⚠️ Inventory alerts unavailable. Please try again.
          </div>
        ) : inventoryAlerts === null ? (
          <div style={{ color: '#6b7280', fontWeight: 900, fontSize: 13, textAlign: 'center' }}>Loading...</div>
        ) : inventoryAlerts.length === 0 ? (
          <div style={{ color: '#39FF14', fontWeight: 900, fontSize: isMobile ? 14 : 16, textAlign: 'center' }}>
            ✅ All inventory levels OK
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {inventoryAlerts.map(item => (
              <div key={item.id} style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'flex-start' : 'center',
                padding: isMobile ? '8px 10px' : '8px 12px',
                minWidth: 0,
                boxSizing: 'border-box',
                background: 'linear-gradient(145deg, #303637, #080a0b)',
                border: '2px solid #ff9800',
                borderRadius: 4,
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,.16), 0 0 5px rgba(255,120,0,.3)'
              }}>
                <span style={{ fontWeight: 900, color: '#ffffff', fontSize: isMobile ? 12 : 14, maxWidth: '100%', overflowWrap: 'anywhere' }}>
                  {item.partName || '(unnamed)'}
                </span>
                <span style={{ fontWeight: 900, color: '#ff9800', fontSize: isMobile ? 12 : 14, whiteSpace: isMobile ? 'normal' : 'nowrap', marginLeft: isMobile ? 0 : 12, marginTop: isMobile ? 4 : 0 }}>
                  QTY: {Number(item.qtyOnHand ?? 0)} / Reorder at: {Number(item.reorderPoint ?? 0)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default Dashboard;
